import { Vector3, Box3, Sphere, OrthographicCamera } from 'three';
import type { BoxHelper, Scene } from 'three';

import ViewPort from '../ViewPort';
import type { PART_TYPES } from '../constants';
import {
  ORTHOGRAPHIC_CAMERA_DEFAULTS,
  TWEEN_TIMES,
  PART_VIEWER_MODES,
} from '../constants';
import { getMidpoint, isPerpendicular } from '../geom-utils';
import type { IntersectionTest } from './intersection-utils';
import type { Renderer } from './renderer';
import {
  makeCameraPositionTween,
  makeControlTargetTween,
  makeViewportTween,
} from './tween-utils';

export const handleZoomOnCursor = async (
  mX: number,
  mY: number,
  dollyDelta: number,
  renderer: Renderer,
): void => {
  if (renderer.trackball.enabled && !renderer.trackball.noZoom) return;

  const oldWidth = renderer.camera.right - renderer.camera.left;
  const oldHeight = renderer.camera.top - renderer.camera.bottom;
  const newHeight = oldHeight / dollyDelta;
  const newWidth = oldWidth / dollyDelta;
  const zoom = renderer.state.cameraZoom * dollyDelta;

  // adjust camera bounds to with zoom to make things appear to zoom to cursor
  const heightDiff = newHeight - oldHeight;
  const widthDiff = newWidth - oldWidth;
  const leftDiff = ((mX + 1.0) / 2.0) * widthDiff;
  const rightDiff = ((1.0 - mX) / 2.0) * widthDiff;
  const bottomDiff = ((mY + 1.0) / 2.0) * heightDiff;
  const topDiff = ((1.0 - mY) / 2.0) * heightDiff;
  renderer.camera.left -= leftDiff;
  renderer.camera.right += rightDiff;
  renderer.camera.bottom -= bottomDiff;
  renderer.camera.top += topDiff;
  await renderer.state.onCameraZoomChange(zoom);
  renderer.state.onOrthoViewChange(ViewPort.fromObject(renderer.camera));

  renderer.camera.updateProjectionMatrix();
};

/**
 * Calculates a position that ensures the camera is located a safe distance
 * outside the bounding sphere of the entire scene to prevent clipping
 * Also returns the distance to the other side of the bounding sphere so we can
 * use that to set the camera.far property, also to prevent clipping
 *
 * @param desiredCameraPosition initial desired camera position
 * @param controlTarget where the camera will look
 * @param scene Scene object containing all objects we need to be weary of
 * clipping
 * @returns { cameraPosition: Vector3, maxDistance: number } New camera position
 * and the maximum distance from the camera position to the other side of the
 * scene's bounding sphere
 */
export const getSafePositionToPreventClippingForOrthographicCamera = ({
  desiredCameraPosition,
  controlTarget,
  scene,
}: {
  desiredCameraPosition: Vector3;
  controlTarget: Vector3;
  scene: Scene;
}): {
  cameraPosition: Vector3;
  maxDistance: number;
} => {
  // Create a bounding sphere around the entire scene
  const box = new Box3().setFromObject(scene);
  const boundingSphere = box.getBoundingSphere(new Sphere());

  // Calculate the direction vector from the target to the initial position
  const direction = new Vector3()
    .subVectors(desiredCameraPosition, controlTarget)
    .normalize();

  // Check if the initial position is inside the bounding sphere
  const distanceFromCenter = desiredCameraPosition.distanceTo(
    boundingSphere.center,
  );
  let newPosition;

  if (distanceFromCenter < boundingSphere.radius) {
    // If inside, find the intersection of the direction vector with the bounding sphere
    const intersectionPoint = new Vector3().addVectors(
      boundingSphere.center,
      direction.multiplyScalar(boundingSphere.radius),
    );

    // Set the camera position to be slightly outside the bounding sphere
    const distanceOutside = boundingSphere.radius * 0.1;
    newPosition = new Vector3().addVectors(
      intersectionPoint,
      direction.multiplyScalar(distanceOutside),
    );
  } else {
    // If outside, move the camera further away in the same direction
    const distanceOutside = boundingSphere.radius * 0.1;
    newPosition = new Vector3().addVectors(
      desiredCameraPosition,
      direction.multiplyScalar(distanceOutside),
    );
  }

  // Calculate the furthest distance from the new camera position to the other side of the bounding sphere
  const furthestDistance =
    newPosition.distanceTo(boundingSphere.center) + boundingSphere.radius;

  return {
    cameraPosition: newPosition,
    maxDistance: furthestDistance,
  };
};

/**
 * Ensures that the camera.far setting is large enough to be able to see the
 * furthest object in the scene
 */
export const ensureCameraFarSettingCompatibleWithMaxDistance = (
  maxDistance: number,
  camera: OrthographicCamera,
) => {
  if (maxDistance > ORTHOGRAPHIC_CAMERA_DEFAULTS.FAR) {
    console.warn(
      'Camera position is very far out. Scene objects likely very far apart.',
    );
    camera.far = maxDistance + maxDistance * 0.1;
    camera.updateProjectionMatrix();
  }
};

/*
On the intersection detected, shift the control target to that intersection point
while maintaining look orientation, by sliding the camera position accordingly by
the same separation vector.
*/
export const recenterOnIntersection = async <T extends PART_TYPES>(
  intersectionTest: IntersectionTest<T>,
  renderer: Renderer,
): Promise<void> => {
  if (!intersectionTest.object) {
    return;
  } else {
    const finalTarget = intersectionTest.point as Vector3;
    const separationVector = new Vector3().subVectors(
      finalTarget,
      renderer.trackball.target,
    );
    const { cameraPosition: finalPosition, maxDistance } =
      getSafePositionToPreventClippingForOrthographicCamera({
        desiredCameraPosition: new Vector3().addVectors(
          renderer.camera.position,
          separationVector,
        ),
        controlTarget: finalTarget,
        scene: renderer.scene,
      });
    ensureCameraFarSettingCompatibleWithMaxDistance(
      maxDistance,
      renderer.camera,
    );

    // NOTE: we do not have to worry about performing an up tween to keep up and
    // look vector orthogonal because we are keeping the look vector direction the same

    const targetTween = makeControlTargetTween(
      renderer,
      finalTarget,
      TWEEN_TIMES.RECENTER,
    );
    const positionTween = makeCameraPositionTween(
      renderer,
      finalPosition,
      TWEEN_TIMES.RECENTER,
    );

    // recenter the camera viewport to the middle with the same current extents
    // resolved zoom stays the same
    const width = Math.abs(renderer.camera.right - renderer.camera.left);
    const height = Math.abs(renderer.camera.bottom - renderer.camera.top);
    const finalViewport = new ViewPort({
      left: -width / 2,
      right: width / 2,
      top: height / 2,
      bottom: -height / 2,
    });
    const viewportTween = makeViewportTween(
      renderer,
      renderer.state.orthoView.clone(),
      finalViewport,
      TWEEN_TIMES.RECENTER,
    );

    await Promise.all(
      [targetTween, positionTween, viewportTween].map((tween) => tween.start()),
    );

    renderer.requestRenderIfNotRequested();
  }
};

const getDefaultCameraPositionForBoxHelper = (
  boxHelper: BoxHelper,
  midPoint: Vector3,
  viewerMode?: PART_VIEWER_MODES,
  sphereFactor = 1.5,
  is2DFileType = false,
): Vector3 => {
  if (!boxHelper.geometry.boundingSphere) {
    boxHelper.geometry.computeBoundingSphere();
  }
  const viewLength =
    (boxHelper.geometry.boundingSphere as Sphere).radius * sphereFactor;

  // For 2D files (DXF/DWG) or VECTOR_EDIT mode, use straight-on camera looking down Z axis
  const use2DView =
    viewerMode === PART_VIEWER_MODES.VECTOR_EDIT || is2DFileType;

  return use2DView
    ? new Vector3(midPoint.x, midPoint.y, viewLength + 10)
    : new Vector3(
        viewLength + midPoint.x,
        viewLength + midPoint.y,
        viewLength + midPoint.z,
      );
};

/**
 * Extracts default view properties for camera setup.
 * @param viewportBoxHelper - BoxHelper for viewport bounds
 * @param positionBoxHelper - BoxHelper for position bounds
 * @param renderer - The renderer instance
 * @param is2DFileTypeOverride - Optional override for 2D file type detection.
 *   Use this when calling before this.state is updated (e.g., in afterEachPriorityLevelLoaded)
 */
export const extractDefaultViewProps = (
  viewportBoxHelper: BoxHelper,
  positionBoxHelper: BoxHelper,
  renderer: Renderer,
  is2DFileTypeOverride?: boolean,
): {
  cameraPosition: Vector3;
  controlTarget: Vector3;
  orthoView: ViewPort;
  up: Vector3;
  maxSceneDistance: number;
} => {
  const viewGeoBox = viewportBoxHelper.geometry.boundingBox as Box3;
  const controlTarget = getMidpoint(viewGeoBox.max, viewGeoBox.min);
  const sphereFactor = 1.5;

  // Check if we're viewing a 2D file type (DXF/DWG)
  // Use override if provided, otherwise use renderer's cached value
  const is2DFileType = is2DFileTypeOverride ?? renderer.is2DFileType;
  // For 2D files (DXF/DWG) or VECTOR_EDIT mode, use Y-up for proper 2D viewing
  const use2DView =
    renderer.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT || is2DFileType;

  // push the camera far enough back to make sure it does not clip
  const { cameraPosition, maxDistance } =
    getSafePositionToPreventClippingForOrthographicCamera({
      desiredCameraPosition: getDefaultCameraPositionForBoxHelper(
        viewportBoxHelper,
        controlTarget,
        renderer.state.viewerMode,
        sphereFactor,
        is2DFileType,
      ),
      controlTarget,
      scene: renderer.scene,
    });
  // establish viewport to fit to bounding box
  const orthoView = ViewPort.getForBoundingBox(
    viewGeoBox,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  // we want to ensure the camera up is orthogonal to the target vector, but resolved in the positive z direction
  let up: Vector3;
  if (use2DView) {
    up = new Vector3(0, 1, 0);
  } else {
    const targetVector = new Vector3()
      .subVectors(cameraPosition, controlTarget)
      .normalize();
    const defaultUp = new Vector3(0, 0, 1);
    if (!isPerpendicular(defaultUp, targetVector)) {
      const across = new Vector3()
        .crossVectors(targetVector, defaultUp)
        .normalize();
      up = new Vector3().crossVectors(across, targetVector);
    } else {
      up = defaultUp.clone();
    }
  }

  return {
    cameraPosition,
    controlTarget,
    orthoView,
    up,
    maxSceneDistance: maxDistance,
  };
};

/**
 * Sets up camera position and trackball controls for a renderer.
 * @param viewportBoxHelper - BoxHelper for viewport bounds
 * @param positionBoxHelper - BoxHelper for position bounds
 * @param renderer - The renderer instance
 * @param is2DFileTypeOverride - Optional override for 2D file type detection.
 *   Use this when calling before this.state is updated (e.g., in afterEachPriorityLevelLoaded)
 */
export const setUpCameraAndControls = (
  viewportBoxHelper: BoxHelper,
  positionBoxHelper: BoxHelper,
  renderer: Renderer,
  is2DFileTypeOverride?: boolean,
): ViewPort => {
  const { cameraPosition, controlTarget, orthoView, up, maxSceneDistance } =
    extractDefaultViewProps(
      viewportBoxHelper,
      positionBoxHelper,
      renderer,
      is2DFileTypeOverride,
    );
  ensureCameraFarSettingCompatibleWithMaxDistance(
    maxSceneDistance,
    renderer.camera,
  );

  renderer.camera.position.copy(cameraPosition);
  renderer.camera.up.copy(up);

  orthoView.updateObjectProps(renderer.camera);
  renderer.camera.updateProjectionMatrix();

  renderer.trackball.left0 = orthoView.left;
  renderer.trackball.right0 = orthoView.right;
  renderer.trackball.top0 = orthoView.top;
  renderer.trackball.bottom0 = orthoView.bottom;

  renderer.trackball.target = controlTarget;
  renderer.trackball.target0 = controlTarget.clone();
  renderer.trackball.position0 = cameraPosition.clone();
  renderer.trackball.up0 = up.clone();

  // Configure trackball for 2D file viewing (DXF/DWG)
  // Use override if provided, otherwise use renderer's cached value
  const is2DFileType = is2DFileTypeOverride ?? renderer.is2DFileType;
  const isVectorEdit =
    renderer.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT;

  // For 2D files: pan only; rotation only via toolbar buttons
  // For VECTOR_EDIT: disable rotation entirely (existing behavior)
  if (isVectorEdit || is2DFileType) {
    renderer.trackball.noRotate = true;
  } else {
    // 3D files: standard 3D rotation
    renderer.trackball.noRotate = false;
  }

  renderer.state.onControlTargetChange(controlTarget.clone());
  renderer.state.onCameraPositionChange(cameraPosition.clone());
  renderer.state.onCameraUpChange(up.clone());

  return orthoView;
};
